import { useContextMutator } from "@openfeature/react-sdk";
import decode from "jwt-decode";
import { useEffect, useMemo } from "react";
import type { PropsWithChildren } from "react";
import { Navigate, Outlet } from "react-router";
import { to } from "routing/to";
import { useLocalStorageValue } from "../../hooks/useLocalStorageValue";
import { LocalStorageKey } from "../../types";
import type { LocalStorageTokenData } from "../../utils/localStorage/localStorage.types";
import { UserContext } from "./user-context";

export const UserProvider = ({ children }: PropsWithChildren) => {
  const token = useLocalStorageValue(LocalStorageKey.TOKEN);
  const { setContext } = useContextMutator({ defaultContext: true });

  const userContext = useMemo(readUserFromStoredToken, [token]);
  const userRole = userContext?.user.role;

  useEffect(updateFeatureUserRole, [userRole, setContext]);
  useEffect(clearFeatureUserRoleOnUnmount, [setContext]);

  if (!userContext) return <Navigate to={to.login} replace />;

  // Remount pages and editors when the user, login session or role changes.
  const { id, session, role } = userContext.user;
  const userSessionKey = JSON.stringify([id, session, role]);
  return (
    <UserContext.Provider key={userSessionKey} value={userContext}>
      {children ?? <Outlet />}
    </UserContext.Provider>
  );

  function readUserFromStoredToken() {
    if (!token) return undefined;

    try {
      const user = decode<LocalStorageTokenData>(token);
      const tokenHasExpired = user.exp <= Date.now() / 1000;
      if (tokenHasExpired) return undefined;

      return { user, token };
    } catch {
      // A token that cannot be decoded must not open authenticated pages.
      return undefined;
    }
  }

  // Tell OpenFeature the user's role without changing the shop or other fields.
  function updateFeatureUserRole() {
    void setContext(({ userRole: previousRole, ...otherContext }) => {
      if (userRole === undefined) return otherContext;
      return { ...otherContext, userRole };
    });
  }

  function clearFeatureUserRoleOnUnmount() {
    // Clear the role when this provider unmounts, not before every role change.
    return () => {
      void setContext(({ userRole: previousRole, ...otherContext }) => otherContext);
    };
  }
};
